export const BLEED = 12;

// Blue-crystal palette — translucent-blue receiver with a cyan-green LCD.
const BLUE_OUT = '#2f7fd0';   // outer blue bezel stroke
const BLUE_HI = '#8fc8ff';    // light blue highlight line
const SILVER = '#cdd8e6';     // chrome inner line
const SILVER_LO = '#7f93ab';  // dim chrome
const GLASS = '#1a3a5e';      // translucent blue glass fill
const GLASS_HI = '#2f5d8e';   // glass highlight
const LCD_BG = '#0e2a2a';     // dark teal-green LCD
const LCD_EDGE = '#1f5a52';   // LCD inner edge
const CYAN = '#46e8d8';       // LCD cyan-green text
const CYAN_HI = '#9bfff2';    // bright cyan
const CYAN_LO = '#23b8ac';    // dim cyan EQ bar
const LED_BLUE = '#5fb0ff';   // blue LED dot

// A small labeled faceplate button (rounded rect + tiny centered text).
function Btn({ x, y, w, h, label, fill = GLASS, stroke = SILVER_LO, txt = CYAN_HI, fs = 4.4 }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={2.2} ry={2.2} fill={fill} opacity={0.95} />
      <rect x={x} y={y} width={w} height={h} rx={2.2} ry={2.2} fill="none" stroke={stroke} strokeWidth={0.7} opacity={0.85} />
      <text x={x + w / 2} y={y + h / 2 + fs * 0.36} fill={txt} fontSize={fs} fontWeight="800" textAnchor="middle" letterSpacing={0.2}>{label}</text>
    </g>
  );
}

// A thin vertical side rail with two tiny stacked buttons.
function SideRail({ x, y, h, top, bottom }) {
  const railW = 14;
  const railTop = y + 24;
  const railH = h - 48;
  const btnW = 12;
  const btnX = x + (railW - btnW) / 2;
  return (
    <g>
      {/* rail body */}
      <rect x={x} y={railTop} width={railW} height={railH} rx={4} fill={GLASS} opacity={0.9} />
      <rect x={x} y={railTop} width={railW} height={railH} rx={4} fill="none" stroke={BLUE_HI} strokeWidth={0.7} opacity={0.5} />
      <rect x={x + railW / 2 - 0.4} y={railTop + 6} width={0.8} height={railH - 12} fill={SILVER_LO} opacity={0.3} />
      {/* two tiny stacked buttons */}
      <Btn x={btnX} y={railTop + 8} w={btnW} h={9} label={top} fs={3.6} />
      <Btn x={btnX} y={railTop + railH - 17} w={btnW} h={9} label={bottom} fs={3.6} />
    </g>
  );
}

// A blue-crystal "NEONWORX" car deck. A translucent-blue bezel wraps the card;
// the top edge carries a dark teal LCD ("EVENT DISPLAY") flanked by EQ clusters
// with SRC / DISP buttons and blue LEDs; the bottom edge carries the numbered
// preset/function button row around a centered silver NEONWORX logo plate; thin
// blue side rails hold ATT/VOL (left) and ESC/MENU (right). The card center
// stays clear so the event content reads cleanly through the "screen".
export default function HeadUnitFrame({ w, h, radius = 12, accent = CYAN }) {
  const W = w + BLEED * 2;
  const H = h + BLEED * 2;
  const x = BLEED;
  const y = BLEED;
  const r = radius + 1;

  // Top LCD display strip (centered, sits across the top edge inside the bleed).
  const lcdH = 16;
  const lcdY = y - 8;
  const lcdW = w * 0.5;
  const lcdX = x + (w - lcdW) / 2;

  // EQ bar clusters flanking the LCD.
  const eqHeights = [4, 8, 5, 10, 6];
  const eqBarW = 2;
  const eqGap = 1.8;
  const eqBase = lcdY + lcdH - 3;
  const eqTotalW = eqHeights.length * eqBarW + (eqHeights.length - 1) * eqGap;
  const eqLeftX = lcdX - 8 - eqTotalW;
  const eqRightX = lcdX + lcdW + 8;

  // Bottom button row geometry.
  const rowY = y + h + 1;
  const rowH = 9;
  const logoW = 40;
  const logoH = 11;
  const logoX = x + (w - logoW) / 2;
  const logoY = rowY - 1;
  const sideBtnW = 17;
  const sideBtnGap = 4;
  const leftLabels = ['1 DISP', '2 SCN', '3 RPT'];
  const rightLabels = ['4 RDM', '5 D-', '6 D+'];

  return (
    <div style={{ position: 'absolute', top: -BLEED, left: -BLEED, width: W, height: H, pointerEvents: 'none' }}>
      <svg width={W} height={H} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="headunit_bcGlass" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={GLASS_HI} stopOpacity="0.9" />
            <stop offset="1" stopColor={GLASS} stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id="headunit_bcLcd" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={LCD_EDGE} stopOpacity="0.95" />
            <stop offset="0.5" stopColor={LCD_BG} stopOpacity="1" />
            <stop offset="1" stopColor="#06181c" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="headunit_bcLogo" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#eef3fa" stopOpacity="0.95" />
            <stop offset="1" stopColor={SILVER} stopOpacity="0.9" />
          </linearGradient>
        </defs>

        {/* Translucent-blue bezel: outer blue stroke + light blue highlight + thin chrome inner line */}
        <rect x={x - 3} y={y - 3} width={w + 6} height={h + 6} rx={r + 3} ry={r + 3} fill="none" stroke={BLUE_OUT} strokeWidth={3.4} opacity={0.92} />
        <rect x={x - 1.4} y={y - 1.4} width={w + 2.8} height={h + 2.8} rx={r + 1.4} ry={r + 1.4} fill="none" stroke={BLUE_HI} strokeWidth={1} opacity={0.45} />
        <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="none" stroke={SILVER} strokeWidth={0.8} opacity={0.7} />

        {/* Side rails (drawn before top/bottom chrome so corner pieces overlap cleanly) */}
        <SideRail x={x - 5} y={y} h={h} top="ATT" bottom="VOL" />
        <SideRail x={x + w - 9} y={y} h={h} top="ESC" bottom="MENU" />

        {/* ---- TOP BAND ---- */}
        {/* SRC button top-left, DISP button top-right */}
        <Btn x={x + 13} y={y - 7} w={20} h={11} label="SRC" fs={5} />
        <Btn x={x + w - 33} y={y - 7} w={20} h={11} label="DISP" fs={5} />

        {/* blue LED dots near the top corners (inside the SRC/DISP) */}
        <circle cx={x + 39} cy={y - 1.5} r={1.6} fill={LED_BLUE} opacity={0.95} />
        <circle cx={x + 39} cy={y - 1.5} r={3} fill="none" stroke={BLUE_HI} strokeWidth={0.5} opacity={0.4} />
        <circle cx={x + w - 39} cy={y - 1.5} r={1.6} fill={LED_BLUE} opacity={0.95} />

        {/* left EQ cluster */}
        {eqHeights.map((bh, i) => (
          <rect key={`eql${i}`} x={eqLeftX + i * (eqBarW + eqGap)} y={eqBase - bh} width={eqBarW} height={bh} rx={0.5} fill={i % 2 === 0 ? CYAN_HI : CYAN_LO} opacity={0.9} />
        ))}
        {/* right EQ cluster (mirrored heights) */}
        {eqHeights.slice().reverse().map((bh, i) => (
          <rect key={`eqr${i}`} x={eqRightX + i * (eqBarW + eqGap)} y={eqBase - bh} width={eqBarW} height={bh} rx={0.5} fill={i % 2 === 0 ? CYAN_LO : CYAN_HI} opacity={0.9} />
        ))}

        {/* centered LCD display */}
        <g>
          <rect x={lcdX} y={lcdY} width={lcdW} height={lcdH} rx={4} ry={4} fill="url(#headunit_bcLcd)" />
          <rect x={lcdX} y={lcdY} width={lcdW} height={lcdH} rx={4} ry={4} fill="none" stroke={CYAN_LO} strokeWidth={0.8} opacity={0.8} />
          <rect x={lcdX + 1.6} y={lcdY + 1.6} width={lcdW - 3.2} height={lcdH - 3.2} rx={3} ry={3} fill="none" stroke={CYAN} strokeWidth={0.4} opacity={0.25} />
          {/* small tuned/stereo dot at left of LCD */}
          <circle cx={lcdX + 6} cy={lcdY + lcdH / 2} r={1.4} fill={CYAN_HI} opacity={0.95} />
          {/* LCD readout */}
          <text x={lcdX + lcdW / 2 + 3} y={lcdY + lcdH / 2 + 2.6} fill={CYAN} fontSize={7} fontWeight="800" textAnchor="middle" letterSpacing={1.4}>EVENT DISPLAY</text>
        </g>

        {/* ---- BOTTOM BAND ---- */}
        {/* left function buttons */}
        {leftLabels.map((lbl, i) => (
          <Btn key={`lb${i}`} x={x + 13 + i * (sideBtnW + sideBtnGap)} y={rowY} w={sideBtnW} h={rowH} label={lbl} fs={4} />
        ))}
        {/* centered silver NEONWORX logo plate */}
        <g>
          <rect x={logoX} y={logoY} width={logoW} height={logoH} rx={2.5} ry={2.5} fill="url(#headunit_bcLogo)" />
          <rect x={logoX} y={logoY} width={logoW} height={logoH} rx={2.5} ry={2.5} fill="none" stroke={SILVER_LO} strokeWidth={0.7} opacity={0.9} />
          <text x={logoX + logoW / 2} y={logoY + logoH / 2 + 2.4} fill="#13314f" fontSize={5.4} fontWeight="900" textAnchor="middle" letterSpacing={0.6}>NEONWORX</text>
        </g>
        {/* right function buttons */}
        {rightLabels.map((lbl, i) => (
          <Btn key={`rb${i}`} x={x + w - 13 - sideBtnW - (rightLabels.length - 1 - i) * (sideBtnW + sideBtnGap)} y={rowY} w={sideBtnW} h={rowH} label={lbl} fs={4} />
        ))}

        {/* corner power/eject LEDs on the bottom edge */}
        <circle cx={x + 5} cy={rowY + rowH / 2} r={1.4} fill={CYAN_HI} opacity={0.85} />
        <circle cx={x + w - 5} cy={rowY + rowH / 2} r={1.4} fill={LED_BLUE} opacity={0.85} />
      </svg>
    </div>
  );
}
